import React, { useEffect, useState, useRef } from "react";
import { Box, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// Shared Components & Helpers
import CartLoader from "../../../Helper/Loaders/CartLoader";
import {
    getFleetPerformanceAnalyticsList,
    getFleetPerformanceAnalyticsTimeseries,
    getFleetPerformanceAnalyticsListActions
} from "../../../Api/FleetPerformanceApi";
import {
    formatDateLabel,
    formatDateForAPI,
    formatDateRange,
    parseDateRangeFromLabel
} from "../../../Helper/Analytics/AnalyticsHelper";
import FleetChartLegend from "../Components/FleetChartLegend";
import FleetScrollableChartWrapper from "../Components/FleetScrollableChartWrapper";
import {
    calculateNiceScale,
    generateYLabelsWithPositions
} from "../Components/FleetChartScaleUtils";
import { calculateGranularity } from "../Components/FleetCommonFunctions";
import GenericDraggableTableView from "../../../Features/Table/GenericDraggableTable";
import { getColumnsConfig } from "../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import DeviceIDLink from "../../../Features/Table/GenericDraggableTable/Renderers/columnTypes/DeviceIDLink";
import { encryptTheParams } from "../../../Helper/QueryParams/EncryptDecrypt";
import DigitalTwinTableSearch from "../../../Components/DigitalTwinTableSearch";
import FleetDetailsSidebar from "../Components/FleetDetailsSidebar";
import { useFleetChartDrillDown } from "../Components/FleetChartDrillDown/useFleetChartDrillDown";
import FleetChartDrillDownModal from "../Components/FleetChartDrillDown/FleetChartDrillDownModal";
import { FLEET_CHART_CONFIG, ENERGY_CONSUMPTION_BUCKETS } from "./FleetChartConfig";
import NumberFormatter from "../../../Helper/Formatters/NumberFormatter";
import {
    FLEET_PERFORMANCE_TABLE_KEYS,
    useFleetTableConfig,
} from "../fleetPerformanceTableConfig";
import { useSearchableFields, searchFieldsKey } from "../../../Hooks/usePagesContent";

// --- Constants & Config for Table ---

const SEARCHABLE_FIELDS = [
    { key: "licensePlateNo", title: "Bus Number" },
    { key: "deviceID", title: "Device ID" },
    { key: "vin", title: "Chassis Number" },
];

const TABLE_COLUMNS_CONFIG = {
    licencePlatNumber: {
        title: "Bus Number",
        headerLabel: "Bus Number",
        dataIndex: "licencePlatNumber",
        key: "licencePlatNumber",
        width: 200,
        valueRenderingType: "device-id-link",
        fixed: "left",
    },
    AveragekWhPerKm: {
        title: "Avg. Energy Consumed per Km (kWh/km)",
        headerLabel: "Avg. Energy Consumed per Km (kWh/km)",
        dataIndex: "AveragekWhPerKm",
        key: "AveragekWhPerKm",
        width: 200,
        valueRenderingType: "number",
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: true,
    },
    bounds: {
        title: "Bounds of Energy Consumed per Km (kWh/km)",
        headerLabel: "Bounds of Energy Consumed per Km (kWh/km)",
        dataIndex: "bounds",
        key: "bounds",
        width: 250,
        valueRenderingType: "number"
    },
    originDepot: {
        title: "Depot",
        headerLabel: "Depot",
        dataIndex: "originDepot",
        key: "originDepot",
        width: 150,
        textFormat: "title",
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: true,
    },
    originCity: {
        title: "City",
        headerLabel: "City",
        dataIndex: "originCity",
        key: "originCity",
        width: 150,
        textFormat: "title",
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: true,
    },
    originState: {
        title: "State",
        headerLabel: "State",
        dataIndex: "originState",
        key: "originState",
        width: 150,
        textFormat: "title",
        sortDirections: ["ascend", "descend", "ascend"],
        sorter: true,
    }
};

const TABLE_COLUMN_ORDER = ["licencePlatNumber", "AveragekWhPerKm", "bounds", "originDepot", "originCity", "originState"];

/**
 * The FleetPerformance org meta key that can replace this details table's
 * columns. Absent from an org's meta, the config above stands unchanged.
 */
const ORG_META_TABLE_KEY = FLEET_PERFORMANCE_TABLE_KEYS.ENERGY_CONSUMPTION_TRENDS_DETAILS;

// --- Sub-Component: Details Table ---

const EnergyConsumptionDetailsTable = ({ startDate, endDate, globalFilters, searchTerm, appliedFilters }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { tableColumnsConfig, tableColumnOrder, resolveMetaInfo } =
        useFleetTableConfig(ORG_META_TABLE_KEY, TABLE_COLUMNS_CONFIG, TABLE_COLUMN_ORDER);

    const [initialColumns, setInitialColumns] = useState([]);
    const [tableColumns, setTableColumns] = useState([]);
    const [selectedColumnKeys, setSelectedColumnKeys] = useState([]);
    const [columnOrder, setColumnOrder] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [total, setTotal] = useState(0);
    const [sortBy, setSortBy] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [isSelectAll, setIsSelectAll] = useState(false);

    // Keyed on the filters' CONTENT, not the object: the parent builds the
    // globalFilters spread inline, so a fresh identity every render would reset
    // the page on things like collapsing the sidebar. Without it, changing the
    // customer or location scope from the sidebar left the request asking for
    // page 3 of a result set that had just changed underneath it.
    const globalFiltersKey = JSON.stringify(globalFilters);
    useEffect(() => {
        setPageNumber(1);
    }, [searchTerm, appliedFilters, globalFiltersKey]);

    const onBatteryIDClick = (record) => {
        encryptTheParams({
            batteryID: record.id,
            deviceID: record.deviceID,
            prevLink: '/fleet-performance',
            navigateFromTab: true,
        }, navigate, true);
    };

    const customRenderers = {
        'device-id-link': (record, config) => (
            <DeviceIDLink
                record={record}
                config={config}
                onTextClick={() => onBatteryIDClick(record)}
            />
        ),
    };

    useEffect(() => {
        const availableColumns = getColumnsConfig({
            tableColumns: tableColumnsConfig,
            tableColumnOrder,
            customRenderers,
            t,
        });
        setInitialColumns(availableColumns);
        setTableColumns(availableColumns);
        setSelectedColumnKeys(availableColumns.map(col => col.dataIndex));
        setColumnOrder(availableColumns.map(col => col.headerLabel));
    }, [t, tableColumnsConfig, tableColumnOrder]);

    const prevDepsRef = useRef('');
    useEffect(() => {
        const fetchData = async () => {
            const currentDeps = JSON.stringify({ startDate, endDate, globalFilters, searchTerm, pageNumber, rowsPerPage, sortBy, appliedFilters, metaInfo: resolveMetaInfo(['licencePlatNumber', 'originCity', 'originState', 'originDepot', 'deviceID', 'pingStatus']) });
            if (prevDepsRef.current === currentDeps) return;
            prevDepsRef.current = currentDeps;

            setIsLoading(true);
            try {
                const searchID = searchTerm.selectedSearchColumn || "batteryID";
                const filters = (searchTerm?.selectedDevices?.length > 0)
                    ? [{ name: searchID || 'batteryID', values: searchTerm.selectedDevices }]
                    : [];

                if (appliedFilters?.length > 0) {
                    filters.push(...appliedFilters);
                }

                const apiParams = {
                    groupBy: ['batteryID'],
                    select: ['AveragekWhPerKm', 'MinkWhPerKm', 'MaxkWhPerKm'],
                    metaInfo: resolveMetaInfo(['licencePlatNumber', 'originCity', 'originState', 'originDepot', 'deviceID', 'pingStatus']),
                    startDate: formatDateForAPI(startDate),
                    endDate: formatDateForAPI(endDate),
                    filters,
                    pageNum: pageNumber,
                    pageCount: rowsPerPage,
                    skipPagination: false,
                };

                if (sortBy.length > 0) {
                    apiParams.sortBy = JSON.stringify(sortBy);
                }

                const response = await getFleetPerformanceAnalyticsList(globalFilters, apiParams);
                if (response?.responseStatus?.code === 200) {
                    const rawData = response.response?.data || [];
                    const data = rawData.map((item, index) => {
                        const getVal = (type) => item.metricsArray?.find(m => m.type === type)?.value;
                        const minKwh = getVal('MinkWhPerKm');
                        const maxKwh = getVal('MaxkWhPerKm');
                        return {
                            id: item.xLabel || index,
                            ...item.metaInfo,
                            AveragekWhPerKm: getVal('AveragekWhPerKm') !== undefined ? Number(getVal('AveragekWhPerKm')).toFixed(2) : "-",
                            bounds: (minKwh !== undefined && maxKwh !== undefined)
                                ? `${Number(minKwh).toFixed(2)} - ${Number(maxKwh).toFixed(2)}`
                                : "-",
                        };
                    });
                    setTableData(data);
                    setTotal(response.response?.paginationInfo?.total || data.length);
                }
            } catch (error) {
                console.error("Error fetching table data:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [startDate, endDate, globalFilters, searchTerm, pageNumber, rowsPerPage, sortBy, appliedFilters, tableColumnOrder, resolveMetaInfo]);

    const applySelectedColumns = (newSelectedKeys) => {
        const filteredColumns = initialColumns.filter((columnConfig) =>
            newSelectedKeys.includes(columnConfig.dataIndex)
        );
        setTableColumns(filteredColumns);
        setSelectedColumnKeys(newSelectedKeys);
        setColumnOrder(filteredColumns.map((col) => col.headerLabel));
    };

    const onColumnOrderChange = (newTableColumns) => {
        setColumnOrder(newTableColumns.map((col) => col.headerLabel));
    };

    const applyTableAction = async (actionType, extraParams = {}) => {
        const filters = (searchTerm?.selectedDevices?.length > 0)
            ? [{ name: searchTerm.selectedSearchColumn || 'batteryID', values: searchTerm.selectedDevices }]
            : [];

        if (appliedFilters?.length > 0) {
            filters.push(...appliedFilters);
        }

        const apiParams = {
            actionType,
            groupBy: ['batteryID'],
            select: ['AveragekWhPerKm', 'MinkWhPerKm', 'MaxkWhPerKm'],
            metaInfo: resolveMetaInfo(['licencePlatNumber', 'originCity', 'originState', 'originDepot', 'deviceID']),
            startDate: formatDateForAPI(startDate),
            endDate: formatDateForAPI(endDate),
            filters,
            resourceIDs: isSelectAll ? [] : selectedRowKeys,
            selectAll: isSelectAll,
            columns: [
                { dataIndex: "licencePlatNumber", headerLabel: "Bus Number" },
                { dataIndex: "AveragekWhPerKm", headerLabel: "Avg. Energy Consumed per Km (kWh/km)" },
                { dataIndex: "Bounds of Energy Consumed per Km (kWh/km)", headerLabel: "Bounds of Energy Consumed per Km (kWh/km)" },
                { dataIndex: "originDepot", headerLabel: "Depot" },
                { dataIndex: "originCity", headerLabel: "City" },
                { dataIndex: "originState", headerLabel: "State" },
            ],
            columnOrder: ["Bus Number", "Avg. Energy Consumed per Km (kWh/km)", "Bounds of Energy Consumed per Km (kWh/km)", "Depot", "City", "State"],
            ...(sortBy.length > 0 ? { sortBy: JSON.stringify(sortBy) } : {}),
            ...(actionType === "mail" && {
                emailTo: extraParams.emailTo,
                emailCc: extraParams.emailCc,
                subject: extraParams.subject,
                body: extraParams.body,
            }),
        };

        const response = await getFleetPerformanceAnalyticsListActions(globalFilters, apiParams);
        if (response?.responseStatus?.code === 200) {
            return response.response?.data || (actionType === "download" ? [] : null);
        }
        return actionType === "download" ? [] : null;
    };

    const getCSVData = async () => {
        return applyTableAction("download");
    };

    const onSendEmail = async (emailParams) => {
        return applyTableAction("mail", emailParams);
    };

    return (
        <GenericDraggableTableView
            title="Vehicle List"
            count={total}
            tableColumns={tableColumns}
            initialColumns={initialColumns}
            selectedColumns={selectedColumnKeys}
            applySelectedColumns={applySelectedColumns}
            columnOrder={columnOrder}
            onColumnOrderChange={onColumnOrderChange}
            tableData={tableData}
            isLoading={isLoading}
            pageNumber={pageNumber}
            rowsPerPage={rowsPerPage}
            total={total}
            onPageChange={setPageNumber}
            applySort={(key, order) => {
                const direction = order === "asc" ? "ASC" : "DESC";
                setSortBy([{ column: key, direction }]);
                setPageNumber(1);
            }}
            emptyMessage="No vehicles found"
            filters={{}}
            disableSearch={true}
            showPrimaryButton={false}
            containerStyles={{ height: "auto" }}
            tableScroll={{ x: "max-content" }}
            rowSelection={{
                type: "checkbox",
                selectedRowKeys,
                isSelectAll,
                onChange: (keys) => {
                    setSelectedRowKeys(keys);
                },
                onSelect: (_record, selected) => {
                    if (!selected) setIsSelectAll(false);
                },
                onSelectAll: (selected) => {
                    setIsSelectAll(selected);
                    if (!selected) setSelectedRowKeys([]);
                }
            }}
            getTotalRowSelectionCount={() => isSelectAll ? total : selectedRowKeys.length}
            getTotalRowSelection={() => selectedRowKeys}
            onClearSelection={() => {
                setSelectedRowKeys([]);
                setIsSelectAll(false);
            }}
            getCSVData={getCSVData}
            onSendEmail={onSendEmail}
            getCSVFileName={() => `vehicle-energy-consumption-${startDate}-${endDate}.csv`}
        />
    );
};

// --- Sub-Component: Details View ---

export const EnergyConsumptionDetailsView = ({ chartType, onBack, startDate: initialStartDate, endDate: initialEndDate, initialGranularity, allFleets, allOnboardingLocationsHierarchy, globalFilters }) => {
    const searchFields = useSearchableFields(SEARCHABLE_FIELDS);
    const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
    const [startDate, setStartDate] = useState(initialStartDate);
    const [endDate, setEndDate] = useState(initialEndDate);
    const [granularity, setGranularity] = useState(initialGranularity || "Weekly");
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [localHierarchicalLocation, setLocalHierarchicalLocation] = useState(globalFilters?.hierarchicalLocation);
    // The sidebar's Business Segment / Customer Account panels both resolve to fleet
    // ids, which is what becomes the fleetIDs request header.
    const [localFleetIds, setLocalFleetIds] = useState(globalFilters?.fleets);
    const [searchTerm, setSearchTerm] = useState({
        selectedDevices: [],
        selectedSearchColumn: null,
    });

    const handleSearch = (selectedDevices, selectedSearchColumn) => {
        setSearchTerm({ selectedDevices, selectedSearchColumn });
    };

    return (
        <Box sx={{ display: "flex", gap: 3, height: "100%", position: "relative" }}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                    width: isSidebarExpanded ? "calc(100% - 320px)" : "100%",
                    minWidth: 0,
                    transition: "width 0.3s ease-in-out",
                }}
            >
                {/* Top Bar with Back Button and Search */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                    }}
                >
                    {/* Left side - Back button */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            cursor: "pointer",
                            "&:hover": {
                                "& .MuiTypography-root": {
                                    color: "#6D72F6 !important",
                                },
                                "& .MuiSvgIcon-root": {
                                    color: "#6D72F6",
                                },
                            },
                        }}
                        onClick={onBack}
                    >
                        <ChevronLeftIcon sx={{ fontSize: 20, color: "#2E2E2E" }} />
                        <Typography variant="fw500s14lh24c2E2E2E">
                            Back
                        </Typography>
                    </Box>
                </Box>

                <DigitalTwinTableSearch
                    onSearch={handleSearch}
                    containerWidth="80%"
                    key={searchFieldsKey(searchFields)}
                    searchableFields={searchFields}
                />

                <Box
                    sx={{
                        backgroundColor: "#FFFFFF",
                        borderRadius: "8px",
                        padding: "24px 28px",
                        border: "1px solid #E6E6E6"
                    }}
                >
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="fw600s16lh24c444B51" sx={{ color: "#2E2E2E" }}>
                            Energy Consumption per km Trend
                        </Typography>
                    </Box>
                    <EnergyConsumptionPerKmTrendChartView
                        startDate={startDate}
                        endDate={endDate}
                        granularity={granularity}
                        globalFilters={{ ...globalFilters, fleets: localFleetIds, hierarchicalLocation: localHierarchicalLocation }}
                        appliedFilters={appliedFilters}
                        searchTerm={searchTerm}
                        enableDrillDown={false}
                    />
                </Box>

                <EnergyConsumptionDetailsTable
                    startDate={startDate}
                    endDate={endDate}
                    globalFilters={{ ...globalFilters, fleets: localFleetIds, hierarchicalLocation: localHierarchicalLocation }}
                    searchTerm={searchTerm}
                    appliedFilters={appliedFilters}
                />
            </Box>

            <FleetDetailsSidebar
                isExpanded={isSidebarExpanded}
                onToggle={() => setIsSidebarExpanded(!isSidebarExpanded)}
                startDate={startDate}
                endDate={endDate}
                onDateChange={({ startDate, endDate }) => {
                    setStartDate(startDate);
                    setEndDate(endDate);
                }}
                granularity={granularity}
                onGranularityChange={setGranularity}
                allFleets={allFleets}
                allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
                selectedFleetIds={localFleetIds || []}
                selectedHierarchicalLocation={localHierarchicalLocation}
                onFiltersChange={setAppliedFilters}
                onHierarchicalLocationChange={setLocalHierarchicalLocation}
                onFleetIdsChange={setLocalFleetIds}
            />
        </Box>
    );
};

// --- Sub-Component: Chart View ---

export const EnergyConsumptionPerKmTrendChartView = ({ startDate, endDate, globalFilters, appliedFilters, granularity, searchTerm, enableDrillDown = true }) => {
    const [loading, setLoading] = useState(true);
    const [chartData, setChartData] = useState({
        categories: [],
        rawCategories: [],
    });

    const prevDepsRef = useRef('');
    useEffect(() => {
        const fetchChartData = async () => {
            const currentDeps = JSON.stringify({ startDate, endDate, globalFilters, appliedFilters, granularity, searchTerm });
            if (prevDepsRef.current === currentDeps) return;
            prevDepsRef.current = currentDeps;

            try {
                setLoading(true);
                const granularityValue = (granularity || calculateGranularity(startDate, endDate)).toUpperCase();
                const filters = [...(appliedFilters || [])];

                const searchID = searchTerm?.selectedSearchColumn || 'batteryID';
                if (searchTerm?.selectedDevices?.length > 0) {
                    filters.push({ name: searchID, values: searchTerm.selectedDevices });
                }

                const apiParams = {
                    groupBy: ['AverageEnergyConsumedBucket'],
                    select: ['DistinctVehicleCount'],
                    granularity: granularityValue,
                    startDate: formatDateForAPI(startDate),
                    endDate: formatDateForAPI(endDate),
                    filters,
                };

                const response = await getFleetPerformanceAnalyticsTimeseries(globalFilters, apiParams);
                if (response?.responseStatus?.code === 200) {
                    const data = response.response?.data || [];
                    const categories = [];
                    const rawCategories = [];
                    const bucketArrays = Object.fromEntries(
                        ENERGY_CONSUMPTION_BUCKETS.map((b) => [b.backendValue, []])
                    );

                    data.forEach(item => {
                        categories.push(formatDateLabel(item.xLabel));
                        rawCategories.push(item.xLabel);
                        const valueMap = Object.fromEntries(
                            (item.metricsArray || []).map((m) => [m.type, m.value])
                        );
                        ENERGY_CONSUMPTION_BUCKETS.forEach((b) => {
                            bucketArrays[b.backendValue].push(valueMap[b.backendValue] || 0);
                        });
                    });

                    setChartData({ categories, rawCategories, ...bucketArrays });
                }
            } catch (error) {
                console.error("Error fetching trend data:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchChartData();
    }, [startDate, endDate, globalFilters, appliedFilters, granularity, searchTerm]);

    const drillDown = useFleetChartDrillDown({
        startDate,
        endDate,
        globalFilters,
        config: FLEET_CHART_CONFIG.ENERGY_CONSUMPTION_TRENDS,
    });

    const onDataPointSelection = (event, chartContext, config) => {
        const { dataPointIndex, seriesIndex } = config;
        if (dataPointIndex === -1 || seriesIndex === -1) return;

        const seriesName = series[seriesIndex].name;
        const xLabel = chartData.categories[dataPointIndex];
        const rawDate = chartData.rawCategories[dataPointIndex];

        // Parse rawDate (could be "YYYY-MM-DD" or "YYYY-MM-DD - YYYY-MM-DD")
        const { startDate: parsedStart, endDate: parsedEnd } = parseDateRangeFromLabel(rawDate);

        drillDown.handleDataPointClick({
            xLabel,
            seriesName,
            startDate: formatDateForAPI(parsedStart),
            endDate: formatDateForAPI(parsedEnd),
        });
    };

    const maxVal = Math.max(
        ...ENERGY_CONSUMPTION_BUCKETS.flatMap((b) => chartData[b.backendValue] || []),
        0
    );
    const scale = calculateNiceScale(0, maxVal, 6);
    const labels = generateYLabelsWithPositions(scale, 315, 30, 37);

    const chartOptions = {
        chart: {
            id: "energy-consumption-trend-chart",
            type: "bar",
            height: 315,
            toolbar: { show: false },
            events: enableDrillDown ? {
                dataPointSelection: onDataPointSelection
            } : {}
        },
        legend: { show: false },
        colors: ENERGY_CONSUMPTION_BUCKETS.map((b) => b.color),
        plotOptions: { bar: { columnWidth: "24px", borderRadius: 2 } },
        xaxis: { categories: chartData.categories },
        yaxis: { show: false, min: scale.min, max: scale.max, tickAmount: 5 },
        grid: { borderColor: "#F0F0F0", strokeDashArray: 3 },
        dataLabels: { enabled: false },
        tooltip: { shared: true, intersect: false, followCursor: true, y: { formatter: (val) => NumberFormatter(val, 1) + (val === 1 ? " vehicle" : " vehicles") } }
    };

    const series = ENERGY_CONSUMPTION_BUCKETS.map((b) => ({
        name: b.label,
        data: chartData[b.backendValue] || [],
    }));

    if (loading) return <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 365 }}><CartLoader /></Box>;

    if (!chartData.categories || chartData.categories.length === 0) {
        return (
            <Box sx={{ height: 365, display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Typography variant="fw400s14c686868">
                    No chart data available
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 3, width: "100%" }}>
            <Box sx={{ mb: 2 }}>
                <FleetChartLegend
                    legendSeries={ENERGY_CONSUMPTION_BUCKETS.map((b) => ({ name: b.label, color: b.color }))}
                    chartIDs={["energy-consumption-trend-chart"]}
                    centered={true}
                />
            </Box>
            <FleetScrollableChartWrapper
                chartOptions={chartOptions}
                series={series}
                chartType="bar"
                chartHeight={315}
                leftAxis={{ label: "Vehicle Count", labelData: labels.map(l => ({ ...l, value: NumberFormatter(l.value, 1) })) }}
                categoriesCount={chartData.categories.length}
                minWidth={chartData.categories.length * 100}
                drillDownEnabled={enableDrillDown}
            />

            <FleetChartDrillDownModal
                open={drillDown.modalOpen}
                onClose={drillDown.handleModalClose}
                selectedDataPoint={drillDown.selectedDataPoint}
                getTableData={drillDown.getTableData}
                applyTableAction={drillDown.applyTableAction}
                getCSVFileName={drillDown.getCSVFileName}
                tableConfigs={drillDown.getTableConfig(1, drillDown.selectedDataPoint)}
                level2GetTableData={drillDown.level2GetTableData}
                level2ApplyTableAction={drillDown.level2ApplyTableAction}
                level2GetCSVFileName={drillDown.level2GetCSVFileName}
                level2TableConfigs={drillDown.getTableConfig(2, drillDown.selectedDataPoint)}
                title={drillDown.getModalTitle}
                level2Title={drillDown.level2Title}
            />

        </Box>
    );
};

// --- Sub-Component: Chart Shell ---

const EnergyConsumptionPerKmTrendChart = ({ startDate, endDate, globalFilters, onDetailsClick }) => {
    return (
        <Box
            sx={{
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                padding: "24px 22px",
                border: "1px solid #E6E6E6",
                maxWidth: 'calc(100vw - 102px)'
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Typography
                        variant="fw600s16lh24c444B51"
                        sx={{ color: "#2E2E2E" }}
                    >
                        Energy Consumption per km Trend
                    </Typography>
                    {onDetailsClick && (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                cursor: "pointer",
                                "&:hover": {
                                    "& .MuiTypography-root": { color: "#4D44D8" },
                                    "& .MuiSvgIcon-root": { color: "#4D44D8" },
                                },
                            }}
                            onClick={onDetailsClick}
                        >
                            <Typography
                                variant="fw500s12lh20c000000"
                                sx={{ color: "#6D72F6" }}
                            >
                                Details
                            </Typography>
                            <ChevronRightIcon sx={{ color: "#6D72F6", fontSize: "18px" }} />
                        </Box>
                    )}
                </Box>
                <Typography variant="fw400s12c686868" sx={{ lineHeight: "20px" }}>
                    {formatDateRange(startDate, endDate)}
                </Typography>
            </Box>

            {/* Chart */}
            <EnergyConsumptionPerKmTrendChartView
                startDate={startDate}
                endDate={endDate}
                globalFilters={globalFilters}
                enableDrillDown={true}
            />
        </Box>
    );
};

// --- Main Component: EnergyConsumptionTrends ---

const EnergyConsumptionTrends = ({ startDate, endDate, globalFilters = {}, onShowDetails }) => {
    const granularity = calculateGranularity(startDate, endDate);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 'calc(100vw - 102px)' }}>
            <EnergyConsumptionPerKmTrendChart
                startDate={startDate}
                endDate={endDate}
                globalFilters={globalFilters}
                onDetailsClick={onShowDetails ? () => onShowDetails(true, "ENERGY_CONSUMPTION_PER_KM", granularity) : undefined}
            />
        </Box>
    );
};

export default EnergyConsumptionTrends;